import React, { useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useLocation } from 'react-router-dom';
import { fetchSpot, updateSpot, setLastSpotRoute } from '../../store/slices/savedSpotsSlice';
import Header from '../../layout/Header'
import MobileNav from '../../layout/MobileNav'
import PageNavSavedLocation from '../../layout/PageNavSavedLocation';
import DualSlider from '../../components/fields/DualSlider';
import DirectionPicker from '../../components/fields/DirectionPicker';
import SportTypePicker from '../../components/fields/SportTypePicker'
import {Helmet} from "react-helmet";

const SpotEditPreferences = () => {

    const { id } = useParams();
    const location = useLocation();
    const { spots, errors } = useSelector(state => state.savedSpots);
    const currentSpot = id ? spots[id] : null;
    const dispatch = useDispatch();
    
    const [formData, setFormData] = useState({
        name: currentSpot?.name || '',
        preferred_wave_height_min: Number(currentSpot?.preferred_wave_height_min) || 0.5,
        preferred_wave_height_max: Number(currentSpot?.preferred_wave_height_max) || 2,
        preferred_swell_period_min: Number(currentSpot?.preferred_swell_period_min) || 8,
        preferred_swell_period_max: Number(currentSpot?.preferred_swell_period_max) || 16,
        preferred_wind_speed_min: Number(currentSpot?.preferred_wind_speed_min) || 0,
        preferred_wind_speed_max: Number(currentSpot?.preferred_wind_speed_max) || 25,
        preferred_wind_directions: currentSpot?.preferred_wind_directions || [],
        preferred_swell_directions: currentSpot?.preferred_swell_directions || [],
        preferred_sport: currentSpot?.preferred_sport || ''
    });
    const skipNextSaveRef = useRef(true);
    // Guards against React 18 StrictMode's dev-only double-invoke of effects on mount, which
    // otherwise lets a second invocation slip past skipNextSaveRef before the spot ever loads
    // and auto-save the still-blank defaults, overwriting the real saved preferences.
    const spotLoadedRef = useRef(false);

    React.useEffect(() => {
        dispatch(fetchSpot(id));
    }, [dispatch, id]);

    React.useEffect(() => {
        if (id) dispatch(setLastSpotRoute({ spotId: id, path: location.pathname }));
    }, [dispatch, id, location.pathname]);

    React.useEffect(() => {
        if (currentSpot) {
            spotLoadedRef.current = true;
            skipNextSaveRef.current = true;
            setFormData({
                name: currentSpot.name || '',
                preferred_wave_height_min: Number(currentSpot.preferred_wave_height_min) || 1.1,
                preferred_wave_height_max: Number(currentSpot.preferred_wave_height_max) || 3.4,
                preferred_swell_period_min: Number(currentSpot.preferred_swell_period_min) || 8,
                preferred_swell_period_max: Number(currentSpot.preferred_swell_period_max) || 16,
                preferred_wind_speed_min: Number(currentSpot.preferred_wind_speed_min) || 0,
                preferred_wind_speed_max: Number(currentSpot.preferred_wind_speed_max) || 25,
                preferred_wind_directions: currentSpot.preferred_wind_directions || [],
                preferred_swell_directions: currentSpot.preferred_swell_directions || [],
                preferred_sport: currentSpot.preferred_sport || ''
            });
        }
    }, [currentSpot]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Auto-save: debounce so dragging a slider doesn't fire a request per pixel,
    // and skip the save triggered by the initial load-from-server population above.
    React.useEffect(() => {
        if (!spotLoadedRef.current || skipNextSaveRef.current) {
            skipNextSaveRef.current = false;
            return;
        }

        const spotData = {
            preferred_wave_height_min: formData.preferred_wave_height_min,
            preferred_wave_height_max: formData.preferred_wave_height_max,
            preferred_swell_period_min: formData.preferred_swell_period_min,
            preferred_swell_period_max: formData.preferred_swell_period_max,
            preferred_wind_speed_min: formData.preferred_wind_speed_min,
            preferred_wind_speed_max: formData.preferred_wind_speed_max,
            preferred_wind_directions: formData.preferred_wind_directions,
            preferred_swell_directions: formData.preferred_swell_directions,
            preferred_sport: formData.preferred_sport,
        };

        const timeoutId = setTimeout(() => {
            dispatch(updateSpot({ spotId: id, spotData }));
        }, 700);

        return () => clearTimeout(timeoutId);
    }, [formData, dispatch, id]);

    if(!currentSpot){
      return <p>Fetching Data...</p>;
    }

    return (
        <>
            <Helmet>
                <title>Edit Ideal Conditions | Oreca Pro</title>
                <link rel="canonical" href={window.location.href} />
            </Helmet>
            <Header />
            <MobileNav />
            <div className='pt-[76px] pb-[90px] px-4 max-w-[1100px] mx-auto'>
                <h1 className='text-2xl font-semibold mb-1 mt-2'>{currentSpot.name ? currentSpot.name : "Current Spot"}</h1>
                <p className='text-xs text-custom-light-grey'>
                    {Number(currentSpot.latitude).toFixed(2)}, {Number(currentSpot.longitude).toFixed(2)}
                </p>

                <PageNavSavedLocation id={currentSpot.id} />

                <div className='max-w-[600px] mx-auto mt-10'>
                    {errors && (
                        <div style={{ color: 'red', marginBottom: '10px' }}>
                            {Object.values(errors).map((error, index) => (
                                <div key={index}>{error}</div>
                            ))}
                        </div>
                    )}

                    <div className="mb-8">
                        <h2 className="text-xl font-semibold mb-6 text-white">Ideal Conditions</h2>
                        <p className='mb-8'>
                            Set your ideal conditions for this spot. Forecast ratings will reflect how well conditions align with your preferences.
                        </p>
                        
                        <DualSlider
                            min={0}
                            max={15}
                            step={0.1}
                            minValue={formData.preferred_wave_height_min}
                            maxValue={formData.preferred_wave_height_max}
                            onMinChange={(value) => handleInputChange({ target: { name: 'preferred_wave_height_min', value } })}
                            onMaxChange={(value) => handleInputChange({ target: { name: 'preferred_wave_height_max', value } })}
                            label="Ideal Wave Height range"
                            unit="m"
                        />

                        <DualSlider
                            min={0}
                            max={80}
                            step={1}
                            minValue={formData.preferred_wind_speed_min}
                            maxValue={formData.preferred_wind_speed_max}
                            onMinChange={(value) => handleInputChange({ target: { name: 'preferred_wind_speed_min', value } })}
                            onMaxChange={(value) => handleInputChange({ target: { name: 'preferred_wind_speed_max', value } })}
                            label="Ideal Wind Speed range"
                            unit="km/h"
                        />

                        <DualSlider
                            min={0}
                            max={35}
                            step={1}
                            minValue={formData.preferred_swell_period_min}
                            maxValue={formData.preferred_swell_period_max}
                            onMinChange={(value) => handleInputChange({ target: { name: 'preferred_swell_period_min', value } })}
                            onMaxChange={(value) => handleInputChange({ target: { name: 'preferred_swell_period_max', value } })}
                            label="Ideal Swell Period range"
                            unit="s"
                        />

                        <DirectionPicker
                            value={formData.preferred_wind_directions}
                            onChange={(value) => handleInputChange({ target: { name: 'preferred_wind_directions', value } })}
                            label="Ideal Wind Directions"
                        />

                        <DirectionPicker
                            value={formData.preferred_swell_directions}
                            onChange={(value) => handleInputChange({ target: { name: 'preferred_swell_directions', value } })}
                            label="Ideal Swell Directions"
                        />

                        <SportTypePicker
                            value={formData.preferred_sport}
                            onChange={(value) => handleInputChange({ target: { name: 'preferred_sport', value } })}
                            label="What sport is this spot's ideal conditions set for? (optional)"
                        />

                    </div>
                </div>
            </div>
            
        </>
    )
};

export default SpotEditPreferences;
